import { getSkewCorrectedDate, maybeUpdateClockOffset } from './clockOffset.js';

import type { Provider, AwsCredentialIdentity } from '@smithy/types';

export interface RawAwsCredentials {
  access_key_id: string;
  secret_access_key: string;
  session_token: string;
  expiration: string;
}

export interface SessionConfig {
  awsSessionEndpoint: string;
  awsSessionMethod: string;
}

const DEFAULT_SESSION_CONFIG: SessionConfig = {
  awsSessionEndpoint: 'https://accounts.babbel.com/en/aws_sessions.json',
  awsSessionMethod: 'post',
};

const minExpirationWindow = 2 * 60 * 1_000;

const hasTimestamp = (response: unknown): response is { timestamp: string } =>
  typeof response === 'object' && response !== null && 'timestamp' in response;

const isTemporaryAwsCredentials = (response: unknown): response is RawAwsCredentials =>
  typeof response === 'object' &&
  response !== null &&
  'access_key_id' in response &&
  'secret_access_key' in response &&
  'session_token' in response &&
  'expiration' in response;

const fetchCredentials = async (config: SessionConfig): Promise<AwsCredentialIdentity> => {
  const response = await globalThis.fetch(config.awsSessionEndpoint, {
    credentials: 'include',
    method: config.awsSessionMethod.toLocaleUpperCase(),
  });

  if (!response.ok) throw new Error('Failed to fetch credentials');

  const credentials: unknown = await response.json();

  if (response.headers.has('date')) {
    maybeUpdateClockOffset(new Date(response.headers.get('date')!));
  } else if (hasTimestamp(credentials)) {
    // Babbel adds a special timestamp field to the credentials object
    // @todo -- it would be better to allow the date header with (Access-Control-Expose-Headers: date) on the server
    maybeUpdateClockOffset(new Date(credentials.timestamp));
  }

  if (!isTemporaryAwsCredentials(credentials))
    throw new Error('Expecting temporary AWS Credentials');

  return {
    accessKeyId: credentials.access_key_id,
    expiration: new Date(credentials.expiration),
    secretAccessKey: credentials.secret_access_key,
    sessionToken: credentials.session_token,
  };
};

const areCredentialsValid = (credentials?: AwsCredentialIdentity): boolean => {
  if (!credentials) return false;
  if (!credentials.expiration) return true;

  const timeLeft = credentials.expiration.getTime() - getSkewCorrectedDate(new Date()).getTime();

  return timeLeft > minExpirationWindow;
};

/**
 * Resolves temporary AWS credentials from a users session cookie. (Browser only)
 *
 * See [authentication-and-authorization (Web flow)](https://github.com/lessonnine/architecture.doc/blob/18103d8cf10dffa70b98bb1a0be937c6e3fa181f/docs/general/authentication-and-authorization.md#web-flow)
 */
export const fromBabbelSession = (
  config: SessionConfig = DEFAULT_SESSION_CONFIG,
): Provider<AwsCredentialIdentity> => {
  let credentials: Promise<AwsCredentialIdentity> | undefined;

  return async () => {
    // This will get called on each request.

    if (credentials === undefined || !areCredentialsValid(await credentials)) {
      // if the credentials are expired or not yet set
      credentials = fetchCredentials(config).catch((error) => {
        // if the credentials are invalid we reset them
        credentials = undefined;
        throw error;
      });
    }

    // if already know the credentials and they're still valid we return them
    return credentials;
  };
};
